<script lang="ts" generics="T">
  import { type Snippet } from 'svelte';
  import IntersectionTrigger from '$lib/components/common/IntersectionTrigger.svelte';

  interface InfiniteScrollProps {
    loadData: (query: string, page: number, pageSize: number, signal: AbortSignal) => Promise<T[]>;
    pageSize?: number;
    queryString?: string | null;
    minimumQueryStringLength?: number;
    dataRenderer: Snippet<[T[], string, boolean, string | null, Snippet]>;
    loadingIndicator?: Snippet;
  }

  let {
    loadData,
    pageSize = 25,
    queryString,
    minimumQueryStringLength = 3,
    dataRenderer,
    loadingIndicator,
  }: InfiniteScrollProps = $props();

  let listPage: number = $state(0);

  // flag to indicate whether the next page should be loaded. if true, the intersection trigger will be rendered.
  // true until the last page loaded has less than pageSize items
  let shouldLoadNextPage = $state(
    (queryString && queryString.length >= minimumQueryStringLength) || (!queryString && minimumQueryStringLength === 0),
  );

  // flag to indicate whether the intersection trigger is currently intersecting the viewport. set to false when the
  // view leaves the viewport. setting to false pauses the next page load until the intersection triggers again.
  let loaderIsIntersecting = $state(false);

  let debouncedQueryString = $state(queryString ?? '');

  let error: string | null = $state(null);

  let data: T[] = $state([]);

  let abortController: AbortController | undefined = undefined;

  let debounceTimer: NodeJS.Timeout | undefined = undefined;

  async function loadNextPage(query: string) {
    if (abortController !== undefined) return; // prevent duplicate requests
    if (query.length < minimumQueryStringLength) return;
    listPage++;
    abortController = new AbortController();
    try {
      const newData = await loadData(query, listPage, pageSize, abortController.signal);
      data.push(...newData);
      abortController = undefined;
      if (newData.length < pageSize) {
        shouldLoadNextPage = false;
        return;
      }
      if (loaderIsIntersecting) {
        loadNextPage(query);
      }
    } catch (e: unknown) {
      if (!(e instanceof Error) || e.name !== 'AbortError') {
        error = e instanceof Error ? e.message : `${e}`;
        throw e;
      }
    }
  }

  $effect(() => {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer);
    }

    if (queryString === undefined || queryString === null || queryString === debouncedQueryString) {
      // this check prevents a debounce and second data reload on the initial render
      return;
    }

    if (abortController !== undefined) {
      abortController.abort();
      abortController = undefined;
    }

    debounceTimer = setTimeout(() => {
      debouncedQueryString = queryString ?? '';
      data = [];
      shouldLoadNextPage = debouncedQueryString.length >= minimumQueryStringLength;
      listPage = 0;
      loadNextPage(debouncedQueryString);
    }, 500);
  });

  export function prependData(...items: T[]) {
    data = [...items, ...data];
  }

  export function forceRefresh() {
    data = [];
    shouldLoadNextPage = true;
    listPage = 0;
    loadNextPage(debouncedQueryString);
  }
</script>

{#snippet loadingIndicatorIntersectionObserver()}
  <IntersectionTrigger
    onIntersectionBegin={() => {
      loaderIsIntersecting = true;
      loadNextPage(debouncedQueryString);
    }}
    onIntersectionEnd={() => {
      loaderIsIntersecting = false;
    }}
  >
    {@render loadingIndicator?.()}
  </IntersectionTrigger>
{/snippet}

{@render dataRenderer(data, debouncedQueryString, shouldLoadNextPage, error, loadingIndicatorIntersectionObserver)}
